import React, { useEffect, useState, useRef } from "react";
import { Formik, Form } from "formik";
import axios from "axios";
import * as Yup from "yup";
import CustomTextInput from "../../components/common/CustomTextInput";
import CustomButton from "../../components/common/CustomButton/CustomButton";
import CustomSelect from "../../components/common/CustomSelect";
import CustomPaperBox from "../../components/common/CustomPaperBox";
import { getSubCategoriesByIdUrl, updateCourse } from "../../utils/urls";
import { toast } from "react-toastify";

const EditDescriptionTab = ({ courseData, categoryList, onSubmit }) => {
    const [subCategoryList, setSubCategoryList] = useState([]);
    const [imagePreview, setImagePreview] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(null);

    const initialValues = {
        id: courseData?.id || "",
        title: courseData?.title || "",
        category_id:
            courseData?.category_id?.category_id || courseData?.category_id || "",
        sub_category_id:
            (courseData?.sub_category_id || []).map((sc) => sc.sub_category_id) || [],
        description: courseData?.description || "",
        banner_file: null,
    };

    useEffect(() => {
        if (courseData?.banner_file) {
            setImagePreview(courseData.banner_file);
        }
    }, [courseData?.banner_file]);

    useEffect(() => {
        const categoryId =
            courseData?.category_id?.category_id || courseData?.category_id;
        if (categoryId) {
            fetchSubCategories(categoryId);
        }
    }, [courseData?.category_id]);

    const fetchSubCategories = async (categoryId) => {
        try {
            const response = await axios.get(`${getSubCategoriesByIdUrl}/${categoryId}`);
            if (response.data?.success) {
                setSubCategoryList(response.data.data);
            } else {
                setSubCategoryList([]);
                console.error("Subcategories fetch failed:", response.data);
            }
        } catch (error) {
            console.error("Error fetching subcategories:", error.message);
            setSubCategoryList([]);
        }
    };

    const validationSchema = Yup.object({
        title: Yup.string()
            .required("Please fill the Course Name")
            .min(3)
            .max(100)
            .matches(
                /^[a-zA-Z0-9\s.,'()-]+$/,
                "Only alphanumeric characters and . , ' - ( ) are allowed"
            ),
        category_id: Yup.string().required("Please select the Category"),
        sub_category_id: Yup.array().min(1, "Please select at least one Sub-Category"),
        description: Yup.string().required("Please fill the Description"),
        banner_file: Yup.mixed()
            .test("fileFormat", "Only PNG, JPG, and SVG images are allowed", (value) => {
                if (!value) return true;
                return ["image/png", "image/jpeg", "image/svg+xml"].includes(value.type);
            })
            .test("fileSize", "Image must be less than 1MB", (value) => {
                if (!value) return true;
                return value.size <= 1024 * 1024;
            }),
    });

    const handleCategoryChange = (selectedValue, setFieldValue) => {
        setFieldValue("category_id", selectedValue);
        setFieldValue("sub_category_id", []);
        fetchSubCategories(selectedValue);
    };

    const handleUpdate = async (values, action) => {
        setLoadingStatus(action);
        try {
            const payload = new FormData();
            payload.append("id", values.id);
            payload.append("title", values.title || "");
            payload.append("category_id", values.category_id || "");
            payload.append("description", values.description || "");
            payload.append("access_for_all_user", courseData?.access_for_all_user ?? false);
            payload.append(
                "access_for_verified_user",
                courseData?.access_for_verified_user ?? false
            );
            payload.append(
                "availability_schedule",
                courseData?.availability_schedule || "Immediate"
            );
            payload.append("schedule_date", courseData?.schedule_date || "");
            payload.append("status", action === "save" ? "Saved As Draft" : "Published");
            if (values.sub_category_id && values.sub_category_id.length > 0) {
                payload.append("sub_category_id", values.sub_category_id.join(","));
            } else {
                payload.append("sub_category_id", "");
            }
            if (values.banner_file) {
                payload.append("banner_file", values.banner_file);
            }
            const res = await axios.put(updateCourse, payload);
            if (res.data?.success) {
                toast.success(res.data.message || "Course updated successfully");
                if (onSubmit) onSubmit();
            } else {
                toast.error(res.data.message || "Update failed");
                console.error("API response indicated failure:", res.data);
            }
        } catch (error) {
            console.error("Error updating course:", {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
            });
            toast.error(
                error.response?.data?.message || "Failed to update course. Please try again."
            );
        } finally {
            setLoadingStatus(null);
        }
    };

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => handleUpdate(values, "")}
        >
            {({ values, setFieldValue }) => (
                <Form className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 w-full">
                    <CustomPaperBox elevation="2" className="w-full">
                        <div className="space-y-6">
                            <CustomTextInput
                                name="title"
                                label="Course Name"
                                required
                                placeholder="Enter"
                                value={values.title}
                                onChange={(e) => setFieldValue("title", e.target.value)}
                            />

                            <CustomSelect
                                name="category_id"
                                label="Select Category"
                                required
                                placeholder="Choose Category"
                                menuList={categoryList.map((d) => ({
                                    value: d?.category_id,
                                    label: d?.title,
                                }))}
                                value={values.category_id}
                                onChange={(e) => handleCategoryChange(e.target.value, setFieldValue)}
                            />

                            <CustomSelect
                                name="sub_category_id"
                                label="Select Sub-Category"
                                required
                                multiple
                                placeholder="Choose Sub-Category"
                                menuList={subCategoryList.map((d) => ({
                                    value: d?.sub_category_id,
                                    label: d?.title,
                                }))}
                                value={values.sub_category_id}
                                onChange={(e) => setFieldValue("sub_category_id", e.target.value)}
                            />

                            <label className="block text-sm font-medium text-gray-700">
                                Enter Description
                            </label>
                            <textarea
                                name="description"
                                rows={3}
                                required
                                className="w-full border-gray-200 p-3 rounded-lg"
                                value={values.description}
                                onChange={(e) => setFieldValue("description", e.target.value)}
                            />
                        </div>
                    </CustomPaperBox>

                    <CustomPaperBox
                        elevation="2"
                        className="w-full flex flex-col items-center gap-6"
                    >
                        {imagePreview && (
                            <img
                                src={imagePreview}
                                alt="Course Banner"
                                className="w-full max-w-full max-h-[400px] rounded border"
                            />
                        )}

                        <input
                            id="banner-upload"
                            name="banner_file"
                            type="file"
                            accept="image/png, image/jpeg, image/jpg, image/svg+xml"
                            hidden
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    setFieldValue("banner_file", file);
                                    setImagePreview(URL.createObjectURL(file));
                                }
                            }}
                        />

                        <div className="flex gap-4">
                            <CustomButton
                                label="Re-Upload"
                                type="button"
                                variant="outlined"
                                onClick={() => document.getElementById("banner-upload").click()}
                            />
                            <CustomButton
                                label="Remove"
                                type="button"
                                variant="text"
                                onClick={() => {
                                    setImagePreview(null);
                                    setFieldValue("banner_file", null);
                                }}
                            />
                        </div>
                    </CustomPaperBox>

                    <div className="flex justify-end gap-6 mt-4 lg:col-span-2">
                        <CustomButton
                            label="Cancel"
                            type="button"
                            variant="outlined"
                            onClick={() => {
                                onSubmit();
                            }}
                        />
                        <CustomButton
                            label="Update"
                            isLoading={loadingStatus === "update"}
                            disabled={!!loadingStatus}
                            type="submit"
                            variant="contained"
                            onClick={() => handleUpdate(values, "update")}
                        />
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default EditDescriptionTab;
